import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { AdminService } from '../shared/services/admin.service';
import { of } from 'rxjs';
import {
  LoadAdminImportRequestsAction,
  adminTypes as types,
  LoadAdminImportRequestsSuccessAction,
  LoadAdminImportRequestsFailsAction,
  LoadAdminImportRequestSuccessAction,
  LoadAdminImportRequestFailsAction,
  LoadAdminImportRequestAction,
  ProcessAdminImportRequestSuccessAction,
  ProcessAdminImportRequestAction,
  ProcessAdminImportRequestFailsAction,
  LoadAdminPropertiesAction,
  LoadAdminPropertiesSuccessAction,
  LoadAdminPropertiesFailsAction,
  LoadAdminPropertyAction,
  LoadAdminPropertySuccessAction,
  LoadAdminPropertyFailsAction,
  ClearAdminPropertyCacheAction,
  ClearAdminPropertyCacheSuccessAction,
  ClearAdminPropertyCacheFailsAction,
  LoadAdminPropertyExportMapAction,
  LoadAdminPropertyExportMapSuccessAction,
  LoadAdminPropertyExportMapFailsAction,
  LoadAdminPropertyExportMapAsExcelAction,
  LoadAdminPropertyExportMapAsExcelSuccessAction,
  LoadAdminPropertyExportMapAsExcelFailsAction,
  LoadAdminImportRequestDiffAction,
  LoadAdminImportRequestDiffSuccessAction,
  LoadAdminImportRequestDiffFailsAction,
  LoadAdminIntegrationPropertyAction,
  LoadAdminIntegrationPropertyFailsAction,
  LoadAdminIntegrationPropertySuccessAction,
  LoadAdminIntegrationsAction,
  LoadAdminIntegrationsFailsAction,
  LoadAdminIntegrationsSuccessAction,
  CreateAdminIntegrationPropertyAction,
  CreateAdminIntegrationPropertySuccessAction,
  CreateAdminIntegrationPropertyFailsAction,
  UpdateAdminIntegrationPropertyAction,
  UpdateAdminIntegrationPropertySuccessAction,
  UpdateAdminIntegrationPropertyFailsAction,
  CreateAdminIntegrationCredentialAction,
  CreateAdminIntegrationCredentialSuccessAction,
  CreateAdminIntegrationCredentialFailsAction,
  DownloadAssociationListAction,
  DownloadAssociationListSuccessAction,
  DownloadAssociationListFailsAction,
  LoadAdminPortfoliosAction,
  LoadAdminPortfoliosSuccessAction,
  LoadAdminPortfoliosFailsAction,
  CreateAdminPortfolioAction,
  CreateAdminPortfolioSuccessAction,
  CreateAdminPortfolioFailsAction,
  LoadAdminPortfolioAction,
  LoadAdminPortfolioSuccessAction,
  LoadAdminPortfolioFailsAction,
  UpdateAdminPortfolioAction,
  UpdateAdminPortfolioSuccessAction,
  UpdateAdminPortfolioFailsAction,
  DeleteAdminPortfolioLogoAction,
  DeleteAdminPortfolioLogoSuccessAction,
  DeleteAdminPortfolioLogoFailsAction,
  SaveAdminPortfolioInviteAction,
  SaveAdminPortfolioInviteSuccessAction,
  SaveAdminPortfolioInviteFailsAction,
  ClearAdminWorkspaceUserAction,
  UpdateAdminWorkspaceUserAction,
  UpdateAdminWorkspaceUserSuccessAction,
  UpdateAdminWorkspaceUserFailsAction,
  ResendAdminPortfolioInviteSuccessAction,
  ResendAdminPortfolioInviteFailsAction,
  ResendAdminPortfolioInviteAction,
  RevokeAdminPortfolioInviteAction,
  RevokeAdminPortfolioInviteFailsAction,
  RevokeAdminPortfolioInviteSuccessAction,
  LoadAdminUsersAction,
  LoadAdminUsersFailsAction,
  LoadAdminUsersSuccessAction,
  LoadPortfolioUserByIdFailsAction,
  LoadPortfolioUserByIdSuccessAction,
  LoadPortfolioUserByIdAction,
  ToggleAdminPropertyActiveStatusAction,
  ToggleAdminPropertyActiveStatusSuccessAction,
  ToggleAdminPropertyActiveStatusFailsAction,
  ToggleAdminUserActiveStatusAction,
  ToggleAdminUserActiveStatusSuccessAction,
  ToggleAdminUserActiveStatusFailsAction,
  ToggleAdminPortfolioActiveStatusAction,
  ToggleAdminPortfolioActiveStatusSuccessAction,
  ToggleAdminPortfolioActiveStatusFailsAction,
  LoadAdminUserAction,
  LoadAdminUserSuccessAction,
  UpdateAdminUserAction,
  UpdateAdminUserFailsAction,
  UpdateAdminUserSuccessAction,
  ReassignAdminPropertyAction,
  ReassignAdminPropertySuccessAction,
  ReassignAdminPropertyFailsAction,
  UpdateAdminPropertyDetailsAction,
  UpdateAdminPropertyDetailsSuccessAction,
  UpdateAdminPropertyDetailsFailsAction,
  ClearAdminPropertyAction,
  ClearAdminPortfolioAction,
  DuplicateAdminPropertyAction,
  DuplicateAdminPropertySuccessAction,
  DuplicateAdminPropertyFailsAction,
  SetAdminPortfoliosFilterAction,
  RefreshPortfolioAnalyticsAction,
} from '../../admin/admin-store';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AdminUserEditModel, AdminUserListModel, ImportRequest, PropertySearchRequest } from '../shared/models';
import { Property } from '../../property/shared/models';
import { BlobWithFilename } from '../../../shared';
import * as FileSaver from 'file-saver-es';
import { IntegrationProperty } from '../shared/models/integration-property';
import { Integration } from '../shared/models/integration';
import { ToastrService } from 'ngx-toastr';
import { IntegrationCredential } from '../shared/models/integration-credential';
import { Workspace, WorkspaceUser } from 'app/internal/shared';
import { Router } from '@angular/router';
import { Invitation } from 'app/internal/workspace/shared/models';
import { LoadWorkspaceUserPropertiesAction, PropertyInfoChangedAction } from 'app/internal/property/property-store/property.actions';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';
import { getAdminPortfolioFilterSettings, getAdminPropertyFilterSettings, getAdminWorkspace } from './admin.selectors';
import { PropertyService } from 'app/internal/property/shared/services/property.service';
import { Error } from '../../../shared';
import { LoadWorkspaceSettingsAction } from 'app/internal/workspace/workspace-store/workspace.actions';


@Injectable()
export class AdminEffects {

  constructor(private actions$: Actions,
    private adminService: AdminService,
    private propertyService: PropertyService,
    private toastr: ToastrService,
    private router: Router,
    private store: Store<AppState>) { }

  /* Import Requests */
  loadAdminImportRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_IMPORT_REQUEST),
      map(action => (action as LoadAdminImportRequestAction).payload),
      mergeMap((importRequestId: string) =>
        this.adminService.getImportRequest(importRequestId)
          .pipe(
            map((response: ImportRequest) => new LoadAdminImportRequestSuccessAction(response)),
            catchError((error) => of(new LoadAdminImportRequestFailsAction(error)))
          )
      )
    );
  });

  loadAdminImportRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_IMPORT_REQUESTS),
      map(action => (action as LoadAdminImportRequestsAction).payload),
      mergeMap(() =>
        this.adminService.getImportRequests()
          .pipe(
            map((response: ImportRequest[]) => new LoadAdminImportRequestsSuccessAction(response)),
            catchError((error) => of(new LoadAdminImportRequestsFailsAction(error)))
          )
      )
    );
  });

  loadAdminImportRequestDiff$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_IMPORT_REQUEST_DIFF),
      map(action => (action as LoadAdminImportRequestDiffAction).payload),
      mergeMap((payload: { importRequestId: string, fiscalYear: number }) =>
        this.adminService.getImportRequestDiffAsExcel(payload.importRequestId, payload.fiscalYear)
          .pipe(
            map((response: BlobWithFilename) => new LoadAdminImportRequestDiffSuccessAction(response)),
            catchError((error) => of(new LoadAdminImportRequestDiffFailsAction(error)))
          )
      )
    );
  });

  loadAdminImportRequestDiffSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_IMPORT_REQUEST_DIFF_SUCCESS),
      map(action => (action as LoadAdminImportRequestDiffSuccessAction).payload),
      tap((pdf: BlobWithFilename) => { FileSaver.saveAs(pdf, pdf.filename); })
    );
  });

  processAdminImportRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.PROCESS_ADMIN_IMPORT_REQUEST),
      map(action => (action as ProcessAdminImportRequestAction).payload),
      mergeMap((payload) =>
        this.adminService.processImportRequest(payload.importRequestId, payload.fiscalYear)
          .pipe(
            map((response: ImportRequest) => new ProcessAdminImportRequestSuccessAction(response)),
            catchError((error) => of(new ProcessAdminImportRequestFailsAction(error)))
          )
      )
    );
  });

  processAdminImportRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.PROCESS_ADMIN_IMPORT_REQUEST_SUCCESS),
      map(action => (action as ProcessAdminImportRequestSuccessAction).payload),
      tap(() => {
        window.location.reload();
      }),
    );
  }, { dispatch: false }
  );

  /* Properties */
  loadAdminProperties$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_PROPERTIES),
      map(action => (action as LoadAdminPropertiesAction).payload),
      tap((payload: PropertySearchRequest) => {
        this.adminService.setAdminPropertyFilter(payload?.filter);
      }),
      mergeMap((payload: PropertySearchRequest) =>
        this.adminService.getProperties(payload)
          .pipe(
            map((response: Property[]) => new LoadAdminPropertiesSuccessAction(response)),
            catchError((error) => of(new LoadAdminPropertiesFailsAction(error)))
          )
      )
    );
  });

  loadAdminProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_PROPERTY),
      map(action => (action as LoadAdminPropertyAction).payload),
      mergeMap((payload: string) =>
        //this.adminService.getProperty(payload)
        this.propertyService.loadPropertyById(payload)
          .pipe(
            map((response: Property) => new LoadAdminPropertySuccessAction(response)),
            catchError((error) => of(new LoadAdminPropertyFailsAction(error)))
          )
      )
    );
  });

  loadAdminIntegrationProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_INTEGRATION_PROPERTY),
      map(action => (action as LoadAdminIntegrationPropertyAction).payload),
      mergeMap((payload: string) =>
        this.adminService.getIntegrationProperty(payload)
          .pipe(
            map((response: IntegrationProperty) => new LoadAdminIntegrationPropertySuccessAction(response)),
            catchError((error) => of(new LoadAdminIntegrationPropertyFailsAction(error)))
          )
      )
    );
  });

  createAdminIntegrationProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_PROPERTY),
      map(action => (action as CreateAdminIntegrationPropertyAction).payload),
      mergeMap((payload) =>
        this.adminService.createIntegrationProperty(payload.propertyId, payload.integrationProperty)
          .pipe(
            map((response: IntegrationProperty) => new CreateAdminIntegrationPropertySuccessAction(response)),
            catchError((error) => of(new CreateAdminIntegrationPropertyFailsAction(error)))
          )
      )
    );
  });

  createAdminIntegrationPropertySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_PROPERTY_SUCCESS),
      tap(() => {
        this.toastr.success('New Connected App Created Successfully');
      }),
    );
  }, { dispatch: false }
  );

  createAdminIntegrationPropertyFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_PROPERTY_FAILS),
      tap(() => {
        this.toastr.error('Failed to Create Connected App.');
      }),
    );
  }, { dispatch: false }
  );

  updateAdminIntegrationProperty$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_ADMIN_INTEGRATION_PROPERTY),
      map(action => (action as UpdateAdminIntegrationPropertyAction).payload),
      mergeMap((payload) =>
        this.adminService.updateIntegrationProperty(payload.propertyId, payload.integrationProperty)
          .pipe(
            map((response: IntegrationProperty) => new UpdateAdminIntegrationPropertySuccessAction(response)),
            catchError((error) => of(new UpdateAdminIntegrationPropertyFailsAction(error)))
          )
      )
    );
  });

  updateAdminIntegrationPropertySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_ADMIN_INTEGRATION_PROPERTY_SUCCESS),
      tap(() => {
        this.toastr.success('Connected App Updated Successfully');
      }),
    );
  }, { dispatch: false }
  );

  updateAdminIntegrationPropertyFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_ADMIN_INTEGRATION_PROPERTY_FAILS),
      tap(() => {
        this.toastr.error('Failed to Update Connected App.');
      }),
    );
  }, { dispatch: false }
  );

  loadAdminIntegrations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_INTEGRATIONS),
      map(action => (action as LoadAdminIntegrationsAction).payload),
      mergeMap(() =>
        this.adminService.getIntegrations()
          .pipe(
            map((response: Integration[]) => new LoadAdminIntegrationsSuccessAction(response)),
            catchError((error) => of(new LoadAdminIntegrationsFailsAction(error)))
          )
      )
    );
  });

  createAdminIntegrationCredential$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_CREDENTIAL),
      map(action => (action as CreateAdminIntegrationCredentialAction).payload),
      mergeMap((payload) =>
        this.adminService.createIntegrationCredential(payload)
          .pipe(
            map((response: IntegrationCredential) => new CreateAdminIntegrationCredentialSuccessAction(response)),
            catchError((error) => of(new CreateAdminIntegrationCredentialFailsAction(error)))
          )
      )
    );
  });

  createAdminIntegrationCredentialSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_CREDENTIAL_SUCCESS),
      switchMap(() => [
        new LoadAdminIntegrationsAction()
      ]),
      tap(() => {
        this.toastr.success('New Credential Added Successfully.');
      }),
    );
  }
  );

  createAdminIntegrationCredentialFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CREATE_ADMIN_INTEGRATION_CREDENTIAL_FAILS),
      tap(() => {
        this.toastr.error('Failed to Add Credential.');
      }),
    );
  }, { dispatch: false }
  );


  loadAdminPropertyExportMap$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_PROPERTY_EXPORT_MAP),
      map(action => (action as LoadAdminPropertyExportMapAction).payload),
      mergeMap((payload: string) =>
        this.adminService.getPropertyExportMap(payload)
          .pipe(
            map((response: any) => new LoadAdminPropertyExportMapSuccessAction(response)),
            catchError((error) => of(new LoadAdminPropertyExportMapFailsAction(error)))
          )
      )
    );
  });

  loadAdminPropertyExportMapAsExcel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_PROPERTY_EXPORT_MAP_AS_EXCEL),
      map(action => (action as LoadAdminPropertyExportMapAsExcelAction).payload),
      mergeMap((payload: string) =>
        this.adminService.getPropertyExportMapAsExcel(payload)
          .pipe(
            map((response: BlobWithFilename) => new LoadAdminPropertyExportMapAsExcelSuccessAction(response)),
            catchError((error) => of(new LoadAdminPropertyExportMapAsExcelFailsAction(error)))
          )
      )
    );
  });

  loadAdminPropertyExportMapAsExcelSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_PROPERTY_EXPORT_MAP_AS_EXCEL_SUCCESS),
      map(action => (action as LoadAdminPropertyExportMapAsExcelSuccessAction).payload),
      tap((pdf: BlobWithFilename) => { FileSaver.saveAs(pdf, pdf.filename); })
    );
  });

  clearAdminPropertyCache$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.CLEAR_ADMIN_PROPERTY_CACHE),
      map(action => (action as ClearAdminPropertyCacheAction).payload),
      mergeMap((payload: string) =>
        this.adminService.clearPropertyCache(payload)
          .pipe(
            map(() => new ClearAdminPropertyCacheSuccessAction()),
            catchError((error) => of(new ClearAdminPropertyCacheFailsAction(error)))
          )
      )
    );
  });

  downloadAssociationList$ = createEffect(() => this.actions$.pipe(
    ofType(types.DOWNLOAD_ASSOCIATION_LIST),
    map(action => (action as DownloadAssociationListAction).payload),
    mergeMap((payload) =>
      this.adminService
        .downloadAssociationList(payload.integrationId, payload.integrationCredentialId)
        .pipe(
          map((response: BlobWithFilename) => new DownloadAssociationListSuccessAction(response)),
          catchError((error) => of((new DownloadAssociationListFailsAction(error))))
        )
    )
  ));

  downloadAssociationListSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.DOWNLOAD_ASSOCIATION_LIST_SUCCESS),
    map(action => (action as DownloadAssociationListSuccessAction).payload),
    tap((csv: BlobWithFilename) => { FileSaver.saveAs(csv, csv.filename); })
  ));

  toggleAdminPropertyActiveStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.TOGGLE_ADMIN_PROPERTY_ACTIVE_STATUS),
      map(action => (action as ToggleAdminPropertyActiveStatusAction).payload),
      mergeMap((payload: { propertyId: string, isActive: boolean, workspaceId?: string }) =>
        this.adminService.togglePropertyActiveStatus(payload.propertyId, payload.isActive)
          .pipe(
            map(() =>
              new ToggleAdminPropertyActiveStatusSuccessAction({ isActive: payload.isActive, workspaceId: payload.workspaceId })
            ),
            catchError((error) => of(new ToggleAdminPropertyActiveStatusFailsAction(error)))
          )
      )
    );
  });

  toggleAdminPropertyActiveSuccessStatus$ = createEffect(() => this.actions$.pipe(
    ofType(types.TOGGLE_ADMIN_PROPERTY_ACTIVE_STATUS_SUCCESS),
    map((action: ToggleAdminPropertyActiveStatusSuccessAction) => action.payload),
    withLatestFrom(this.store.select(getAdminPropertyFilterSettings)),
    tap(([action]) => {
      if (action.isActive) {
        this.toastr.success('Property Reactivated');
      } else {
        this.toastr.success('Property Deactivated');
      }
    }),
    mergeMap(([payload, propertyFilterSettings]) => {
      if (payload.workspaceId) {
        return [
          new LoadWorkspaceUserPropertiesAction(),
          new LoadAdminPortfolioAction({ workspaceId: payload.workspaceId, includeIntegrationDetail: false })
        ];
      } else {
        return [
          new LoadWorkspaceUserPropertiesAction(),
          new LoadAdminPropertiesAction(new PropertySearchRequest(propertyFilterSettings))
        ];
      }
    }),
  ));

  reassignProperty$ = createEffect(() => this.actions$.pipe(
    ofType(types.REASSIGN_ADMIN_PROPERTY),
    map(action => (action as ReassignAdminPropertyAction).payload),
    mergeMap((payload) =>
      this.adminService.reassignProperty(payload.propertyId, payload.destinationWorkspaceId, payload.includeUsers)
        .pipe(
          map(() => new ReassignAdminPropertySuccessAction()),
          catchError((error) => of((new ReassignAdminPropertyFailsAction(error))))
        )
    ))
  );

  reassignPropertySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.REASSIGN_ADMIN_PROPERTY_SUCCESS),
    map((action: ReassignAdminPropertySuccessAction) => action.payload),
    withLatestFrom(
      this.store.select(getAdminPropertyFilterSettings),
      this.store.select(getAdminWorkspace)
    ),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap(([action, propertyFilterSettings, adminWorkspace]) => {
      this.toastr.success('Property Reassigned');
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mergeMap(([payload, propertyFilterSettings, adminWorkspace]) => {

      const actions = [
        new LoadWorkspaceUserPropertiesAction(),
        new LoadAdminPropertiesAction(new PropertySearchRequest(propertyFilterSettings))
      ];

      // The Property was re-assigned from the Portfolio Edit page, so refresh the Portfolio.
      if (adminWorkspace) {
        actions.push(new LoadAdminPortfolioAction({ workspaceId: adminWorkspace?.workspaceId, includeIntegrationDetail: false }));
      }

      return actions;
    }),
  ));

  updateAdminPropertyDetails$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_PROPERTY_DETAILS),
    map(action => (action as UpdateAdminPropertyDetailsAction).payload),
    mergeMap(payload =>
      this.propertyService.savePropertyDetails(payload.property)
        .pipe(
          map((response: Property) =>
            new UpdateAdminPropertyDetailsSuccessAction({ property: response, workspaceId: payload.workspaceId })),
          catchError((error) => of(new UpdateAdminPropertyDetailsFailsAction(error)))
        )
    ))
  );

  updateAdminPropertyDetailsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_PROPERTY_DETAILS_SUCCESS),
    map(action => (action as UpdateAdminPropertyDetailsSuccessAction).payload),
    tap((payload) => {
      this.toastr.success('Property Details Saved');
      if (payload.workspaceId) {
        this.router.navigate(['/useraccount/admin/portfolios/' + payload.workspaceId], { queryParams: { tabIndex: '1' } });
      } else {
        this.router.navigate(['/useraccount/admin/properties']);
      }
    }),
    mergeMap((payload) => {
      return [
        new ClearAdminPropertyAction(),
        new ClearAdminPortfolioAction(),
        new PropertyInfoChangedAction(payload.property.propertyId),
        new LoadWorkspaceUserPropertiesAction()
      ];
    }),
  ));

  duplicateProperty$ = createEffect(() => this.actions$.pipe(
    ofType(types.DUPLICATE_ADMIN_PROPERTY),
    map(action => (action as DuplicateAdminPropertyAction).payload),
    mergeMap((payload) =>
      this.adminService.duplicateProperty(payload.propertyId)
        .pipe(
          map(() => new DuplicateAdminPropertySuccessAction()),
          catchError((error) => of((new DuplicateAdminPropertyFailsAction(error))))
        )
    ))
  );

  duplicatePropertySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.DUPLICATE_ADMIN_PROPERTY_SUCCESS),
    map((action: DuplicateAdminPropertySuccessAction) => action.payload),
    withLatestFrom(this.store.select(getAdminPropertyFilterSettings)),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tap(([action]) => {
      this.toastr.success('Property Duplicated');
      this.router.navigate(['/useraccount/admin/properties']);
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mergeMap(([payload, propertyFilterSettings]) => {
      return [
        new LoadWorkspaceUserPropertiesAction(),
        new LoadAdminPropertiesAction(new PropertySearchRequest(propertyFilterSettings))
      ];
    }),
  ));

  duplicatePropertyFailed$ = createEffect(() => this.actions$.pipe(
    ofType(types.DUPLICATE_ADMIN_PROPERTY_FAILS),
    map(action => (action as DuplicateAdminPropertyFailsAction).payload),
    tap((errors: Error[]) => {
      const msg = (errors && errors.length > 0) ? errors[0]?.details : '';
      this.toastr.error(msg, 'Property Not Duplicated', { timeOut: 0, extendedTimeOut: 0 });
    })
  ), { dispatch: false }
  );

  /* Portfolios (Workspaces) */
  loadAdminWorkspaces$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_ADMIN_PORTFOLIOS),
    map(action => (action as LoadAdminPortfoliosAction).payload),
    mergeMap(() =>
      this.adminService.getWorkspaces()
        .pipe(
          map((response: Workspace[]) => new LoadAdminPortfoliosSuccessAction(response)),
          catchError((error) => of((new LoadAdminPortfoliosFailsAction(error))))
        )
    ))
  );

  setAdminPortfoliosFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(types.SET_ADMIN_PORTFOLIOS_FILTER),
      map((action: SetAdminPortfoliosFilterAction) => action.payload),
      tap((payload) => {
        this.adminService.setAdminPortfolioFilter(payload);
      }),
      mergeMap(() => [
        new LoadAdminPortfoliosAction()
      ])
    )
  );

  createAdminWorkspace$ = createEffect(() => this.actions$.pipe(
    ofType(types.CREATE_ADMIN_PORTFOLIO),
    map(action => (action as CreateAdminPortfolioAction).payload),
    mergeMap((payload) =>
      this.adminService.createWorkspace(payload)
        .pipe(
          map((response: Workspace) => new CreateAdminPortfolioSuccessAction(response)),
          catchError((error) => of((new CreateAdminPortfolioFailsAction(error))))
        )
    ))
  );

  loadAdminWorkspace$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_ADMIN_PORTFOLIO),
    map(action => (action as LoadAdminPortfolioAction).payload),
    mergeMap((payload) =>
      this.adminService.getWorkspaceById(payload.workspaceId, payload.includeIntegrationDetail)
        .pipe(
          map((response: Workspace) => new LoadAdminPortfolioSuccessAction(response)),
          catchError((error) => of((new LoadAdminPortfolioFailsAction(error))))
        )
    )
  ));

  updateAdminWorkspace$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_PORTFOLIO),
    map(action => (action as UpdateAdminPortfolioAction).payload),
    mergeMap((payload) =>
      this.adminService.updateWorkspace(payload)
        .pipe(
          map((response: Workspace) => new UpdateAdminPortfolioSuccessAction(response)),
          catchError((error) => of((new UpdateAdminPortfolioFailsAction(error))))
        )
    ))
  );

  updateAdminWorkspaceSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_ADMIN_PORTFOLIO_SUCCESS),
      map(action => (action as UpdateAdminPortfolioSuccessAction).payload),
      tap(() => {
        this.toastr.success('Portfolio Updated');
        this.router.navigateByUrl('/useraccount/admin/portfolios');
      }),
      mergeMap(() => [
        new LoadWorkspaceSettingsAction()
      ])
    );
  });

  deleteLogo$ = createEffect(() => this.actions$.pipe(
    ofType(types.DELETE_ADMIN_PORTFOLIO_LOGO),
    map(action => (action as DeleteAdminPortfolioLogoAction).payload),
    mergeMap((payload) =>
      this.adminService.removeLogo(payload.workspaceId, payload.isHeaderLogo)
        .pipe(
          map(() => new DeleteAdminPortfolioLogoSuccessAction({ isHeaderLogo: payload.isHeaderLogo, workspaceId: payload.workspaceId })),
          catchError((error) => of((new DeleteAdminPortfolioLogoFailsAction(error))))
        )
    )
  ));

  deleteLogoSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.DELETE_ADMIN_PORTFOLIO_LOGO_SUCCESS),
    map((action: DeleteAdminPortfolioLogoSuccessAction) => action.payload),
    tap(() => {
      this.toastr.success('Logo Removed');
    }),
    mergeMap((payload) => [
      new LoadAdminPortfolioAction({ workspaceId: payload.workspaceId, includeIntegrationDetail: false })
    ]),
  ));

  updateUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_PORTFOLIO_USER),
    map(action => (action as UpdateAdminWorkspaceUserAction).payload),
    mergeMap((payload) =>
      this.adminService.updateWorkspaceUser(payload.workSpaceUser, payload.workSpaceId)
        .pipe(
          map((response: WorkspaceUser) => new UpdateAdminWorkspaceUserSuccessAction({ workSpaceUser: response, workSpaceId: payload.workSpaceId })),
          catchError((error) => of((new UpdateAdminWorkspaceUserFailsAction(error))))
        )
    )
  ));

  updateUserSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_PORTFOLIO_USER_SUCCESS),
    map((action: UpdateAdminWorkspaceUserSuccessAction) => action.payload),
    tap((payload) => {
      this.toastr.success('User Details Saved');
      this.router.navigate(['/useraccount/admin/portfolios', payload.workSpaceId], { queryParams: { tabIndex: '2' } });
    }),
    map(() => new ClearAdminWorkspaceUserAction())
  )
  );

  inviteUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_ADMIN_PORTFOLIO_INVITE),
    map(action => (action as SaveAdminPortfolioInviteAction).payload),
    mergeMap((payload) =>
      this.adminService.inviteUser(payload.invite, payload.workSpaceId)
        .pipe(
          map((response: Invitation) => new SaveAdminPortfolioInviteSuccessAction({ invite: response, workSpaceId: payload.workSpaceId })),
          catchError((error) => of((new SaveAdminPortfolioInviteFailsAction(error))))
        )
    )
  ));

  inviteUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(types.SAVE_ADMIN_PORTFOLIO_INVITE_SUCCESS),
      map(action => (action as SaveAdminPortfolioInviteSuccessAction).payload),
      tap(() => {
        this.toastr.success('User Invited');
      }),
      mergeMap((payload) => [
        new LoadAdminPortfolioAction({ workspaceId: payload.workSpaceId, includeIntegrationDetail: false })
      ])
    )
  );

  resendInvite$ = createEffect(() => this.actions$.pipe(
    ofType(types.RESEND_ADMIN_PORTFOLIO_INVITE),
    map(action => (action as ResendAdminPortfolioInviteAction).payload),
    mergeMap((payload) =>
      this.adminService.resendInvite(payload.workspaceUserInvitationId, payload.workSpaceId)
        .pipe(
          map(() => new ResendAdminPortfolioInviteSuccessAction(payload.workSpaceId)),
          catchError((error) => of((new ResendAdminPortfolioInviteFailsAction(error))))
        )
    )
  ));

  resendInviteSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.RESEND_ADMIN_PORTFOLIO_INVITE_SUCCESS),
      map(action => (action as ResendAdminPortfolioInviteSuccessAction).payload),
      tap(() => {
        this.toastr.success('Invitation Resent');
      })
    );
  }, { dispatch: false }
  );

  revokeInvite$ = createEffect(() => this.actions$.pipe(
    ofType(types.REVOKE_ADMIN_PORTFOLIO_INVITE),
    map(action => (action as RevokeAdminPortfolioInviteAction).payload),
    mergeMap((payload) =>
      this.adminService.revokeInvite(payload.workspaceUserInvitationId)
        .pipe(
          map(() => new RevokeAdminPortfolioInviteSuccessAction(payload.workspaceUserInvitationId)),
          catchError((error) => of((new RevokeAdminPortfolioInviteFailsAction(error))))
        )
    )
  ));

  revokeInviteSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REVOKE_ADMIN_PORTFOLIO_INVITE_SUCCESS),
      map(action => (action as RevokeAdminPortfolioInviteSuccessAction).payload),
      tap(() => {
        this.toastr.success('Invitation Revoked');
      })
    );
  }, { dispatch: false }
  );

  toggleAdminPortfolioActiveStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.TOGGLE_ADMIN_PORTFOLIO_ACTIVE_STATUS),
      map(action => (action as ToggleAdminPortfolioActiveStatusAction).payload),
      mergeMap((payload: { workspaceId: string, isActive: boolean }) =>
        this.adminService.toggleWorkspaceActiveStatus(payload.workspaceId, payload.isActive)
          .pipe(
            map(() =>
              new ToggleAdminPortfolioActiveStatusSuccessAction({ isActive: payload.isActive })
            ),
            catchError((error) => of(new ToggleAdminPortfolioActiveStatusFailsAction(error)))
          )
      )
    );
  });

  toggleAdminPortfolioActiveSuccessStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(types.TOGGLE_ADMIN_PORTFOLIO_ACTIVE_STATUS_SUCCESS),
      map((action: ToggleAdminPortfolioActiveStatusSuccessAction) => action.payload),
      withLatestFrom(this.store.select(getAdminPortfolioFilterSettings)),
      tap(([payload]) => {
        if (payload.isActive) {
          this.toastr.success('Portfolio Reactivated');
        } else {
          this.toastr.success('Portfolio Deactivated');
        }
      }),
      mergeMap((portfolioFilterSettings) => [
        new LoadWorkspaceUserPropertiesAction(),
        new LoadAdminUsersAction(),
        new LoadAdminPortfoliosAction(portfolioFilterSettings)
      ])
    )
  );

  refreshPortfolioAnalyticsAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.REFRESH_PORTFOLIO_ANALYTICS),
      map(action => (action as RefreshPortfolioAnalyticsAction).workspaceId),
      tap(() => {
        this.toastr.warning(
          'Portfolio Analytics Refresh has initiated.  Please note this may take sevaral minutes to complete!', '',
          {
            positionClass: 'toast-center-center',
            timeOut: 5000,
            closeButton: true,
          });
      }),
      mergeMap((payload) =>
        this.adminService.refreshPortfolioAnalytics(payload)
          .pipe(
            map((response: boolean) => of(response)),
            catchError((error) => of(error))
          )
      )
    );
  }, { dispatch: false });

  /* Users */
  loadAdminUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.LOAD_ADMIN_USERS),
      map(action => (action as LoadAdminUsersAction).payload),
      tap((payload: string) => {
        this.adminService.setAdminUserFilter(payload);
      }),
      mergeMap(() =>
        this.adminService.getUsers()
          .pipe(
            map((response: AdminUserListModel[]) => new LoadAdminUsersSuccessAction(response)),
            catchError((error) => of(new LoadAdminUsersFailsAction(error)))
          )
      )
    );
  });

  loadAdminUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_ADMIN_USER),
    map(action => (action as LoadAdminUserAction).payload),
    mergeMap((payload) =>
      this.adminService.getUser(payload)
        .pipe(
          map((response: AdminUserEditModel) => new LoadAdminUserSuccessAction(response)),
          catchError((error) => of((new LoadAdminUsersFailsAction(error))))
        )
    )
  ));

  loadPortfolioUserById$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_PORTFOLIO_USER_BY_ID),
    map(action => (action as LoadPortfolioUserByIdAction).payload),
    mergeMap((payload) =>
      this.adminService.getUserByWorkspaceId(payload.userId, payload.workspaceId)
        .pipe(
          map((response: WorkspaceUser) => new LoadPortfolioUserByIdSuccessAction(response)),
          catchError((error) => of((new LoadPortfolioUserByIdFailsAction(error))))
        )
    )
  ));

  toggleAdminUserActiveStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.TOGGLE_ADMIN_USER_ACTIVE_STATUS),
      map(action => (action as ToggleAdminUserActiveStatusAction).payload),
      mergeMap((payload: { userId: string, isActive: boolean, workspaceId: string }) =>
        this.adminService.toggleWorkspaceUserActiveStatus(payload.userId, payload.workspaceId, payload.isActive).pipe(
          map(() =>
            new ToggleAdminUserActiveStatusSuccessAction({ isActive: payload.isActive, workspaceId: payload.workspaceId })
          ),
          catchError((error) => of(new ToggleAdminUserActiveStatusFailsAction(error)))
        )
      )
    );
  });

  toggleAdminUserActiveSuccessStatus$ = createEffect(() => this.actions$.pipe(
    ofType(types.TOGGLE_ADMIN_USER_ACTIVE_STATUS_SUCCESS),
    map((action: ToggleAdminUserActiveStatusSuccessAction) => action.payload),
    tap((payload) => {
      if (payload.isActive) {
        this.toastr.success('User Reactivated');
      } else {
        this.toastr.success('User Deactivated');
      }
    }),
    mergeMap((payload) => [
      new LoadWorkspaceUserPropertiesAction(),
      new LoadAdminPortfolioAction({ workspaceId: payload.workspaceId, includeIntegrationDetail: false })
    ]),
  ));

  updateAdminUser$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_ADMIN_USER),
    map(action => (action as UpdateAdminUserAction).payload),
    mergeMap((payload) =>
      this.adminService.updateUser(payload)
        .pipe(
          map((response: AdminUserEditModel) => new UpdateAdminUserSuccessAction(response)),
          catchError((error) => of((new UpdateAdminUserFailsAction(error))))
        )
    ))
  );

  updateAdminUserSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_ADMIN_USER_SUCCESS),
      map(action => (action as UpdateAdminUserSuccessAction).payload),
      tap(() => {
        this.toastr.success('User Updated');
      }),
      mergeMap(() => [
        //new LoadAdminUsersAction()
      ]),
    );
  });
}
